import { useState, useEffect, useRef } from "react";
import ProviderOrganizations from "../provider/ProviderOrganizations";
import { organizationNames, escapeHtml } from "../provider/organizationHelpers";

import api from "../../services/api";

const LEAFLET_CSS = `https://unpkg.com/leaflet@1.9.4/dist/leaflet.css`;
const LEAFLET_JS = `https://unpkg.com/leaflet@1.9.4/dist/leaflet.js`;

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Fraunces:ital,opsz,wght@0,9..144,600;1,9..144,300&display=swap');
  .pf-root { font-family: 'DM Sans', system-ui, sans-serif; }
  @keyframes pfFadeUp  { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
  @keyframes pfSlideIn { from{opacity:0;transform:translateX(-12px)} to{opacity:1;transform:translateX(0)} }
  @keyframes pfPulse   { 0%,100%{transform:scale(1)} 50%{transform:scale(1.05)} }
  @keyframes pfSpin    { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
  .pf-card { transition:all 0.2s ease; cursor:pointer; }
  .pf-card:hover { transform:translateY(-2px); box-shadow:0 8px 32px rgba(0,0,0,0.12) !important; }
  .pf-card.selected { border-color:#1A5EA8 !important; background:#f8faff !important; }
  .pf-filter-btn { transition:all 0.2s; cursor:pointer; }
  .pf-filter-btn:hover { background:rgba(102,126,234,0.1) !important; }
  .pf-filter-btn.active { background:linear-gradient(135deg,#1A5EA8,#0B2545) !important; color:white !important; border-color:transparent !important; }
  .pf-search-btn { transition:all 0.2s; }
  .pf-search-btn:hover:not(:disabled) { transform:translateY(-2px); box-shadow:0 8px 24px rgba(102,126,234,0.45) !important; }
  .pf-copilot-btn { transition:all 0.2s; }
  .pf-copilot-btn:hover { opacity:0.85; transform:translateY(-1px); }
  #pf-map { z-index:1; }
  .pf-map-container .leaflet-container { border-radius:16px; }
  .pf-input {
    width:100%; padding:10px 14px; border:1px solid #e5e7eb;
    border-radius:10px; font-size:14px; font-family:inherit;
    outline:none; box-sizing:border-box; transition:border-color 0.2s;
  }
  .pf-input:focus { border-color:#1A5EA8; }
  .pf-label {
    font-size:11px; font-weight:700; color:#6b7280;
    text-transform:uppercase; letter-spacing:0.08em;
    display:block; margin-bottom:6px;
  }
`;

const SPECIALTIES = [
  { label: "All Specialists", value: "" },
  { label: "Nephrology", value: "Nephrology" },
  { label: "Cardiology", value: "Cardiology" },
  { label: "Endocrinology", value: "Endocrinology" },
  { label: "Urology", value: "Urology" },
  { label: "Internal Medicine", value: "Internal Medicine" },
  { label: "Transplant Surgery", value: "Transplant Surgery" },
];

const RADII = [
  { label: "10 miles", value: 16 },
  { label: "25 miles", value: 40 },
  { label: "50 miles", value: 80 },
  { label: "100 miles", value: 161 },
];

function Spinner({ size = 20 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      border: "2px solid #e5e7eb", borderTopColor: "#1A5EA8",
      animation: "pfSpin 0.7s linear infinite", flexShrink: 0
    }} />
  );
}

function SpecialtyBadge({ specialty }) {
  const colors = {
    "Nephrology": { bg: "#dbeafe", color: "#2563eb" },
    "Cardiology": { bg: "#fee2e2", color: "#dc2626" },
    "Endocrinology": { bg: "#fce7f3", color: "#db2777" },
    "Urology": { bg: "#fef3c7", color: "#d97706" },
    "Internal Medicine": { bg: "#d1fae5", color: "#059669" },
    "Transplant Surgery": { bg: "#fef3c7", color: "#b45309" },
  };
  const c = colors[specialty] || { bg: "#eef2ff", color: "#1A5EA8" };
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 999,
      background: c.bg, color: c.color,
      textTransform: "uppercase", letterSpacing: "0.06em"
    }}>
      {specialty || "Specialist"}
    </span>
  );
}

export default function ProviderFinder() {
  const mapObjRef = useRef(null);
  const markersRef = useRef([]);

  const [nameSearch, setNameSearch] = useState("");
  const [zip, setZip] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [radius, setRadius] = useState(80);
  const [nephrologyOnly, setNephrologyOnly] = useState(false);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [searched, setSearched] = useState(false);
  const [leafletLoaded, setLeafletLoaded] = useState(false);
  const [locating, setLocating] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  const [allProviders, setAllProviders] = useState([]);
  // ── Load Leaflet ────────────────────────────────────────────────────────────
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet"; link.href = LEAFLET_CSS;
    document.head.appendChild(link);
    if (window.L) { setLeafletLoaded(true); }
    else {
      const script = document.createElement("script");
      script.src = LEAFLET_JS;
      script.onload = () => setLeafletLoaded(true);
      document.head.appendChild(script);
    }
    setTimeout(() => setAnimateIn(true), 100);
  }, []);

  // ── Init map ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!leafletLoaded || mapObjRef.current) return;
    const L = window.L;
    const map = L.map("pf-map", { zoomControl: true }).setView([39.8283, -98.5795], 4);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 18,
    }).addTo(map);
    mapObjRef.current = map;
  }, [leafletLoaded]);

  // ── Update markers ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapObjRef.current || !window.L || providers.length === 0) return;
    const L = window.L;
    const map = mapObjRef.current;
    markersRef.current.forEach(m => map.removeLayer(m));
    markersRef.current = [];
    const bounds = [];

    providers.forEach((p, i) => {
      const lat = parseFloat(p.latitude);
      const lon = parseFloat(p.longitude);
      if (!lat || !lon || isNaN(lat) || isNaN(lon) ||
        lat < -90 || lat > 90 || lon < -180 || lon > 180) return;

      const isNephrology = p.nephrologySpecialist;
      const isSelected = selected?.npi === p.npi;
      const size = isNephrology ? 36 : 28;

      const icon = L.divIcon({
        className: "",
        html: `<div style="
          width:${size}px;height:${size}px;border-radius:50%;
          background:${isNephrology ? "linear-gradient(135deg,#1A5EA8,#0B2545)" : "linear-gradient(135deg,#1B6CA8,#0ab5a0)"};
          border:${isSelected ? "3px solid #fbbf24" : "2px solid white"};
          box-shadow:0 2px 8px rgba(0,0,0,0.25);
          display:flex;align-items:center;justify-content:center;
          color:white;font-size:${isNephrology ? 16 : 12}px;font-weight:700;cursor:pointer;
        ">${isNephrology ? "🩺" : (i + 1)}</div>`,
        iconSize: [size, size], iconAnchor: [size / 2, size / 2],
      });

      const marker = L.marker([lat, lon], { icon })
        .addTo(map)
        .bindPopup(`
          <div style="font-family:'DM Sans',sans-serif;min-width:200px;padding:4px;">
            <div style="font-weight:700;font-size:14px;color:#0D2B45;margin-bottom:4px;">
              ${p.fullName || `${p.firstName || ""} ${p.lastName || ""}`}
            </div>
            <div style="font-size:12px;color:#1A5EA8;margin-bottom:4px;">${p.specialty || ""}</div>
            <div style="font-size:11px;color:#6b7280;">${organizationNames(p).map(escapeHtml).join(" · ")}</div>
            <div style="font-size:11px;color:#6b7280;">${p.addressLine1 || ""}, ${p.city || ""}, ${p.state || ""}</div>
            ${p.phone ? `<div style="font-size:11px;color:#374151;margin-top:4px;">📞 ${p.phone}</div>` : ""}
            ${p.distanceKm ? `<div style="font-size:11px;color:#059669;margin-top:4px;">📍 ${(p.distanceKm * 0.621371).toFixed(1)} miles</div>` : ""}
            ${isNephrology ? `<div style="font-size:10px;font-weight:700;color:#0B2545;margin-top:6px;">🩺 Nephrology Specialist</div>` : ""}
          </div>
        `);
      marker.on("click", () => setSelected(p));
      markersRef.current.push(marker);
      bounds.push([lat, lon]);
    });

    if (bounds.length > 0) map.fitBounds(bounds, { padding: [40, 40] });
  }, [providers, selected]);

  // ── Search helpers ──────────────────────────────────────────────────────────
  const searchByCoords = async (lat, lon, state = null) => {
    setLoading(true); setError(null); setSearched(true);
    try {
      const res = await api.get("/v1/providers/nearby", {
        params: {
          lat, lon, radius, specialty: specialty || undefined,
          nephrologyOnly, limit: 30, state: state || undefined
        }
      });
      setAllProviders(res.data || []);
      setProviders(res.data || []);
      if ((res.data || []).length === 0)
        setError("No providers found in this area. Try increasing the radius.");
    } catch { setError("Could not load providers. Please try again."); }
    finally { setLoading(false); }
  };

  const searchByZip = async () => {
    if (!zip || zip.length < 5) { setError("Please enter a valid 5-digit zip code."); return; }
    setLoading(true); setError(null); setSearched(true);
    try {
      const geoRes = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (!geoRes.ok) { setError("Invalid zip code."); setLoading(false); return; }
      const geoData = await geoRes.json();
      const lat = parseFloat(geoData.places[0].latitude);
      const lon = parseFloat(geoData.places[0].longitude);
      const state = geoData.places[0]["state abbreviation"];
      if (mapObjRef.current) mapObjRef.current.setView([lat, lon], 10);
      await searchByCoords(lat, lon, state);
    } catch { setError("Could not geocode zip code. Please try again."); setLoading(false); }
  };

  const searchByName = async () => {
    if (!nameSearch || nameSearch.trim().length < 2) return;
    setLoading(true); setError(null); setSearched(true);
    try {
      const res = await api.get("/v1/providers/search", { params: { name: nameSearch.trim() } });
      setAllProviders(res.data || []);
      setProviders(res.data || []);
      if ((res.data || []).length === 0)
        setError(`No providers found matching "${nameSearch}"`);
      if (mapObjRef.current && res.data?.length > 0) {
        const withCoords = res.data.filter(p => p.latitude && p.longitude);
        if (withCoords.length > 0)
          mapObjRef.current.setView([withCoords[0].latitude, withCoords[0].longitude], 10);
      }
    } catch { setError("Could not search by name. Please try again."); }
    finally { setLoading(false); }
  };

  const handleSearch = () => nameSearch.trim().length > 1 ? searchByName() : searchByZip();

  const getUserLocation = () => {
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      pos => { setLocating(false); searchByCoords(pos.coords.latitude, pos.coords.longitude); },
      () => { setLocating(false); setError("Could not get your location. Please enter a zip code."); }
    );
  };

  const handleCopilot = (p) => {
    const prompt = `I found Dr. ${p.fullName || p.lastName}, ${p.specialty} at ${p.groupName || p.addressLine1} in ${p.city}, ${p.state}. What questions should I ask about my kidney disease?`;
    window.dispatchEvent(new CustomEvent("copilot-preload", { detail: { prompt } }));
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="pf-root" style={{ background: "#f8faff", minHeight: "100vh", paddingBottom: 80 }}>
      <style>{STYLES}</style>

      {/* Header */}
      <div style={{
        background: "linear-gradient(135deg,#0D2B45 0%,#1a3a5c 50%,#2d1b69 100%)",
        padding: "24px 24px 32px", position: "relative",
        animation: animateIn ? "pfFadeUp 0.5s ease both" : "none",
      }}>
        <div style={{
          position: "absolute", inset: 0, opacity: 0.04, pointerEvents: "none",
          backgroundImage: "radial-gradient(circle,white 1px,transparent 1px)",
          backgroundSize: "28px 28px",
        }} />
        <div style={{ maxWidth: 900, margin: "0 auto", position: "relative" }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            background: "rgba(102,126,234,0.2)", border: "1px solid rgba(102,126,234,0.4)",
            borderRadius: 999, padding: "4px 14px", marginBottom: 16,
          }}>
            <div style={{
              width: 6, height: 6, borderRadius: "50%", background: "#10b981",
              animation: "pfPulse 2s infinite"
            }} />
            <span style={{
              fontSize: 11, fontWeight: 700, color: "#a78bfa",
              textTransform: "uppercase", letterSpacing: "0.1em"
            }}>Provider Finder</span>
          </div>
          <h1 style={{
            fontFamily: "'Fraunces',serif", fontSize: "clamp(24px,4vw,40px)",
            fontWeight: 700, color: "white", margin: "0 0 10px", lineHeight: 1.2
          }}>
            Find a Nephrology Specialist
          </h1>
          <p style={{
            fontSize: 15, color: "rgba(255,255,255,0.6)", margin: 0,
            fontWeight: 300, maxWidth: 520
          }}>
            Search our database of nephrologists and related specialists — cardiology, endocrinology, urology, and transplant surgery — near you.
          </p>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 20px", position: "relative", zIndex: 10 }}>

        {/* Search panel */}
        <div style={{
          background: "white", borderRadius: 20, padding: "24px 24px 20px",
          boxShadow: "0 8px 40px rgba(0,0,0,0.12)", marginBottom: 24,
          animation: animateIn ? "pfFadeUp 0.4s ease 0.1s both" : "none",
        }}>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>

            {/* Name */}
            <div style={{ flex: "2 1 200px", minWidth: 160 }}>
              <label className="pf-label">Search by Name</label>
              <input className="pf-input" type="text" value={nameSearch}
                onChange={e => setNameSearch(e.target.value)}
                onKeyPress={e => e.key === "Enter" && handleSearch()}
                placeholder="e.g. Sarswat or Nitasha" />
            </div>

            {/* Zip */}
            <div style={{ flex: "1 1 120px", minWidth: 110 }}>
              <label className="pf-label">Zip Code</label>
              <input className="pf-input" type="text" maxLength={5} value={zip}
                onChange={e => setZip(e.target.value.replace(/\D/g, ""))}
                onKeyPress={e => e.key === "Enter" && handleSearch()}
                placeholder="e.g. 60637" />
            </div>

            {/* Specialty */}
            <div style={{ flex: "1 1 160px", minWidth: 140 }}>
              <label className="pf-label">Specialty</label>
              <select className="pf-input" value={specialty}
                onChange={e => setSpecialty(e.target.value)}
                style={{ background: "white" }}>
                {SPECIALTIES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>

            {/* Distance */}
            <div style={{ flex: "1 1 120px", minWidth: 110 }}>
              <label className="pf-label">Distance</label>
              <select className="pf-input" value={radius}
                onChange={e => setRadius(Number(e.target.value))}
                style={{ background: "white" }}>
                {RADII.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>

            {/* Nephrology toggle */}
            <div style={{ flex: "0 0 auto" }}>
              <label className="pf-label">Filter</label>
              <button className={`pf-filter-btn ${nephrologyOnly ? "active" : ""}`}
                onClick={() => {
                  const newVal = !nephrologyOnly;
                  setNephrologyOnly(newVal);
                  if (newVal) {
                    setProviders(allProviders.filter(p => p.nephrologySpecialist));
                  } else {
                    setProviders(allProviders);
                  }
                }}
                style={{
                  padding: "10px 14px", border: "1px solid #e5e7eb", borderRadius: 10,
                  fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                  background: nephrologyOnly ? "" : "white",
                  color: nephrologyOnly ? "" : "#6b7280", whiteSpace: "nowrap",
                }}>
                🩺 Nephrology Only
              </button>
            </div>

            {/* Buttons */}
            <div style={{ flex: "0 0 auto", display: "flex", gap: 8 }}>
              <button className="pf-search-btn" onClick={handleSearch}
                disabled={loading || (!zip && !nameSearch)}
                style={{
                  padding: "10px 22px", border: "none", borderRadius: 10,
                  fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                  display: "flex", alignItems: "center", gap: 8, color: "white",
                  background: ((!zip && !nameSearch) || loading)
                    ? "rgba(102,126,234,0.35)"
                    : "linear-gradient(135deg,#1A5EA8,#0B2545)",
                  boxShadow: (zip || nameSearch) && !loading
                    ? "0 4px 16px rgba(102,126,234,0.4)" : "none",
                }}>
                {loading ? <Spinner size={15} /> : "🔍"} Search
              </button>
              <button className="pf-search-btn" onClick={getUserLocation}
                disabled={loading || locating}
                style={{
                  padding: "10px 14px", background: "white",
                  color: "#1A5EA8", border: "1px solid rgba(102,126,234,0.3)",
                  borderRadius: 10, fontSize: 13, fontWeight: 600,
                  cursor: (loading || locating) ? "not-allowed" : "pointer",
                  fontFamily: "inherit", display: "flex", alignItems: "center", gap: 6,
                }}>
                {locating ? <Spinner size={13} /> : "📍"} My Location
              </button>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div style={{
              marginTop: 12, padding: "10px 14px", background: "#fef2f2",
              border: "1px solid #fca5a5", borderRadius: 10, fontSize: 13, color: "#dc2626"
            }}>
              {error}
            </div>
          )}
        </div>

        {/* Map + Results */}
        <div style={{
          display: "grid", gridTemplateColumns: "1fr 380px", gap: 20,
          animation: animateIn ? "pfFadeUp 0.4s ease 0.2s both" : "none",
        }}>

          {/* Map */}
          <div className="pf-map-container" style={{
            background: "white", borderRadius: 20,
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)", overflow: "hidden", minHeight: 540,
          }}>
            <div id="pf-map" style={{ height: "100%", minHeight: 540, width: "100%" }} />
          </div>

          {/* Results */}
          <div style={{
            display: "flex", flexDirection: "column", gap: 12,
            maxHeight: 600, overflowY: "auto", paddingRight: 4
          }}>

            {loading && (
              <div style={{
                display: "flex", flexDirection: "column", alignItems: "center",
                justifyContent: "center", padding: 60, gap: 12
              }}>
                <Spinner size={32} />
                <p style={{ fontSize: 13, color: "#9ca3af", margin: 0 }}>Finding providers...</p>
              </div>
            )}

            {!loading && providers.length > 0 && (
              <div style={{
                fontSize: 12, fontWeight: 600, color: "#6b7280",
                padding: "0 4px", display: "flex", justifyContent: "space-between"
              }}>
                <span>{providers.length} providers found</span>
                {nephrologyOnly && <span style={{ color: "#0B2545" }}>🩺 Specialists only</span>}
              </div>
            )}

            {!loading && searched && providers.length === 0 && !error && (
              <div style={{ textAlign: "center", padding: "40px 20px", color: "#9ca3af" }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>🔍</div>
                <p style={{ fontSize: 14, fontWeight: 600, color: "#374151" }}>No providers found</p>
                <p style={{ fontSize: 13 }}>Try increasing the radius or a different name</p>
              </div>
            )}

            {!loading && providers.map((p, i) => (
              <div key={p.npi || i}
                className={`pf-card ${selected?.npi === p.npi ? "selected" : ""}`}
                onClick={() => setSelected(p)}
                style={{
                  background: "white", borderRadius: 14, padding: "16px 18px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                  border: p.nephrologySpecialist
                    ? "1px solid #e5e7eb" : "1px solid #f3f4f6",
                  borderLeft: p.nephrologySpecialist
                    ? "4px solid #1A5EA8" : "1px solid #f3f4f6",
                  animation: `pfSlideIn 0.3s ease ${Math.min(i * 0.04, 0.4)}s both`,
                }}>

                <div style={{
                  display: "flex", justifyContent: "space-between",
                  alignItems: "flex-start", gap: 8, marginBottom: 6
                }}>
                  <div>
                    <p style={{ fontSize: 14, fontWeight: 700, color: "#0D2B45", margin: "0 0 4px" }}>
                      {p.fullName || `${p.firstName || ""} ${p.lastName || ""}`.trim()}
                    </p>
                    <SpecialtyBadge specialty={p.specialty} />
                  </div>
                  {p.nephrologySpecialist && (
                    <span style={{
                      fontSize: 9, fontWeight: 700, padding: "2px 8px",
                      borderRadius: 999, background: "#f5f3ff", color: "#0B2545",
                      border: "1px solid #e9d5ff", whiteSpace: "nowrap", flexShrink: 0
                    }}>
                      🩺 Nephrology
                    </span>
                  )}
                </div>

                <ProviderOrganizations provider={p} />

                <p style={{ fontSize: 11, color: "#9ca3af", margin: "0 0 10px" }}>
                  📍 {[p.addressLine1, p.city, p.state, p.zip].filter(Boolean).join(", ")}
                </p>

                <div style={{
                  display: "flex", justifyContent: "space-between",
                  alignItems: "center", flexWrap: "wrap", gap: 6
                }}>
                  <div style={{ display: "flex", gap: 12 }}>
                    {p.distanceKm != null && (
                      <span style={{ fontSize: 11, color: "#059669", fontWeight: 600 }}>
                        {(p.distanceKm * 0.621371).toFixed(1)} mi
                      </span>
                    )}
                    {p.phone && (
                      <a href={`tel:${p.phone}`} onClick={e => e.stopPropagation()}
                        style={{ fontSize: 11, color: "#1A5EA8", textDecoration: "none", fontWeight: 600 }}>
                        📞 {p.phone}
                      </a>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    {p.cmsProfileUrl && (
                      <a href={p.cmsProfileUrl} target="_blank" rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        style={{
                          fontSize: 11, color: "#6b7280", textDecoration: "none",
                          padding: "3px 8px", border: "1px solid #e5e7eb", borderRadius: 6, background: "white"
                        }}>
                        CMS ↗
                      </a>
                    )}
                    <button className="pf-copilot-btn"
                      onClick={e => { e.stopPropagation(); handleCopilot(p); }}
                      style={{
                        fontSize: 11, fontWeight: 600, padding: "3px 10px",
                        background: "linear-gradient(135deg,#1A5EA8,#0B2545)",
                        color: "white", border: "none", borderRadius: 6,
                        cursor: "pointer", fontFamily: "inherit"
                      }}>
                      🤝 Ask Copilot
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Empty state */}
        {!searched && !loading && (
          <div style={{
            textAlign: "center", padding: "48px 20px",
            animation: animateIn ? "pfFadeUp 0.4s ease 0.3s both" : "none"
          }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🗺️</div>
            <p style={{
              fontFamily: "'Fraunces',serif", fontSize: 22, fontWeight: 600,
              color: "#0D2B45", margin: "0 0 8px"
            }}>
              Find Specialists Near You
            </p>
            <p style={{ fontSize: 14, color: "#9ca3af", maxWidth: 400, margin: "0 auto" }}>
              Enter your zip code or use your location to find nephrology specialists nearby.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}